import React from 'react';
import StockPositionGrid from 'components/molecules/StockPositionGrid';
import { postExportStockGrid } from 'services/stock-position';
import { openInNewTab } from 'helpers';
import { IData, IRow } from 'components/molecules/StockPositionGrid/types';
import { useSelectFilter } from 'hooks/planning/useFilter';
import qs from 'querystring';
import { useQuery } from 'react-query';
import { postGridStockPosition } from 'services/stock-position/grid-stock-position';
import { STOCK_POSITION_GRID_VALIDATION_RULES } from 'constants/grid/validationRules';
import { columns } from './data';

const TAKE = 10;
const MAX_ROWS = 100000;

const mapRows = result => {
  return result.map(row => {
    return {
      id: row.id,
      installation: row.installation,
      itemCode: row.itemCode,
      itemDescription: row.itemDescription,
      model: row.model,
      supplier: row.supplier,
      materialClass: row.materialClass,
      group: row.group,
      materialClassId: row.materialClassId,
      groupId: row.groupId,
      currentQuantity: row.currentQuantity,
      minimumQuantity: row.minimumQuantity,
      maximumQuantity: row.maximumQuantity,
      orderNumber: row.orderNumber,
      warehouse: row.warehouse,
      local: row.local,
      costCenter: row.costCenter,
      unityValue: row.unityValue,
      totalValue: row.totalValue,
      validity: row.validity,
      leadtime: row.leadtime,
      lifespan: row.lifespan,
      validityLifespan: row.validityLifespan,
      removalDate: row.removalDate,
      entryDate: row.entryDate,
      owner: row.owner,
      situation: row.situation,
      oracleCode: row.oracleCode,
    };
  });
};
const StockPositionGridContainer = ({
  setUpdatedAt,
}: {
  setUpdatedAt: React.Dispatch<React.SetStateAction<Date | null>>;
}) => {
  // INÍCIO ESTADO DOS FILTROS
  const [exportingStockData, setExportingStockData] =
    React.useState<boolean>(false);
  const [dataGrid, setDataGrid] = React.useState(() => columns);
  // INÍCIO ESTADO DOS ARGUMENTOS DE PAGINAÇÃO DO GRID
  const [totalRows, setTotalRows] = React.useState(MAX_ROWS);
  const [currentPage, setCurrentPage] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<number>(TAKE);
  const pageSizes = React.useMemo(() => [10, 20, 30, 40, 50, 100], []);
  // FIM ESTADO DOS ARGUMENTOS DE PAGINAÇÃO DO GRID

  const [
    installationFilterOptionsContext,
    materialClassFilterOptionsContext,
    groupFilterOptionsContext,
    descriptionItemFilterOptionsContext,
  ] = [
    useSelectFilter('installation'),
    useSelectFilter('material-class'),
    useSelectFilter('group'),
    useSelectFilter('description'),
  ];

  const allFilterContexts = React.useMemo(
    () => [
      installationFilterOptionsContext,
      materialClassFilterOptionsContext,
      groupFilterOptionsContext,
      descriptionItemFilterOptionsContext,
    ],
    [
      installationFilterOptionsContext,
      materialClassFilterOptionsContext,
      groupFilterOptionsContext,
      descriptionItemFilterOptionsContext,
    ],
  );

  const gridOrder = React.useMemo(
    () =>
      dataGrid
        .filter(column => column.direction)
        .map(column => ({
          columnName: column.name,
          order: column?.direction?.toLocaleUpperCase(),
        })),
    [dataGrid],
  );

  // INÍCIO ESTADO DAS LINHAS DO GRID
  const [gridRows, setGridRows] = React.useState<IRow[]>([]);
  // FIM ESTADO DAS LINHAS DO GRID

  const installationIds = installationFilterOptionsContext.map(item =>
    Number(item.value),
  );
  const materialClassIds = materialClassFilterOptionsContext.map(item =>
    Number(item.value),
  );
  const stockGroupIds = groupFilterOptionsContext.map(item =>
    Number(item.value),
  );
  const descriptionItens = descriptionItemFilterOptionsContext.map(
    item => item.label,
  );

  const exportStockData = React.useCallback(() => {
    setExportingStockData(true);

    const params = {
      order: gridOrder,
      installationIds,
      materialClassIds,
      stockGroupIds,
      descriptionItens,
    };
    // TODO: seria interessante alterar o contrato no backend
    // para retornar as mensagens para o usuário
    postExportStockGrid(params)
      .then(({ url }) => {
        openInNewTab(url);
      })
      .finally(() => setExportingStockData(false));
  }, [
    gridOrder,
    installationIds,
    materialClassIds,
    stockGroupIds,
    descriptionItens,
  ]);

  // AO ALTERAR OS FILTROS, DEVERÁ RETORNAR PARA PÁGINA INICIAL
  React.useEffect(() => {
    setCurrentPage(0);
  }, [allFilterContexts, pageSize]);

  const queryStockPositionGrid = useQuery({
    queryKey: [
      'StockPositionGridDataContext',
      [pageSize, currentPage, gridOrder, ...allFilterContexts],
    ],
    queryFn: async () => {
      const qsparams = qs.stringify({
        page: currentPage + 1,
        take: pageSize,
      });
      const params = {
        order: gridOrder,
        installationIds,
        materialClassIds,
        stockGroupIds,
        descriptionItens,
      };

      const { data } = await postGridStockPosition(qsparams, params);
      return data;
    },
    select: data => {
      const rows: IRow[] = mapRows(data.result);
      return {
        count: data.count,
        rows,
      };
    },
    onSuccess: ({ count, rows }: { rows: IRow[]; count: number }) => {
      setGridRows(rows);
      setTotalRows(count);
    },
    enabled: true,
    cacheTime: 0,
  });

  const getRowId = React.useCallback((row: IRow) => String(row.id), []);

  const getRowById = React.useCallback(
    id => {
      const strId = `${id}`;
      return gridRows.find(row => `${row?.id}` === strId);
    },
    [gridRows],
  );

  const onSortingChange = React.useCallback(sortedList => {
    setDataGrid(oldDataGrid =>
      oldDataGrid.map(item => {
        const foundItem = sortedList.find(
          sortItem => sortItem.columnName === item.name,
        );
        if (foundItem) {
          return {
            ...item,
            direction: foundItem.direction,
          };
        }
        const newItem = { ...item };
        delete newItem.direction;
        return newItem;
      }),
    );
  }, []);

  const refetch = React.useCallback(() => {
    queryStockPositionGrid.refetch();
    setUpdatedAt(new Date());
  }, [queryStockPositionGrid, setUpdatedAt]);

  return (
    <StockPositionGrid
      columns={dataGrid as IData[]}
      rows={gridRows}
      dayMonthYearCells={['validity', 'validityLifespan']}
      entryDateCells={['entryDate']}
      removalDateCells={['removalDate']}
      loading={
        queryStockPositionGrid?.isLoading ||
        queryStockPositionGrid?.isFetching ||
        exportingStockData
      }
      onSortingChange={onSortingChange}
      currentPage={currentPage}
      pageSize={pageSize}
      pageSizes={pageSizes}
      totalCount={totalRows}
      blrCurrecyCells={['unityValue', 'totalValue']}
      quantityCells={[
        'currentQuantity',
        'minimumQuantity',
        'maximumQuantity',
        'leadtime',
        'lifespan',
      ]}
      validationRules={STOCK_POSITION_GRID_VALIDATION_RULES}
      tooltipMaxStrLength={50}
      refetch={refetch}
      getRowId={getRowId}
      getRowById={getRowById}
      onCurrentPageChange={setCurrentPage}
      onPageSizeChange={setPageSize}
      onExport={exportStockData}
    />
  );
};

export default StockPositionGridContainer;
